import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import { isStringArray } from '@/api/queries/utils';
import { SessionStorageKey } from '@/hooks/useSessionStorage';
import { FilterType } from '@/models/filter/FilterType';
import { isFilterType } from '@/utils/filter/isFilterType';

export type LocalOptionPathsByFilter = Partial<Record<FilterType, string[]>>;

const STORAGE_KEY = SessionStorageKey.LOCAL_FILTER_OPTION_PATHS;
const EMPTY_PATHS: string[] = [];

const isLocalOptionPathsByFilter = (value: unknown): value is LocalOptionPathsByFilter =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.entries(value).every(([key, paths]) => isFilterType(key) && isStringArray(paths));

const hasSamePaths = (left: readonly string[] | undefined, right: readonly string[]): boolean =>
  left !== undefined && left.length === right.length && left.every((path, index) => path === right[index]);

interface LocalOptionPathsState {
  pathsByFilter: LocalOptionPathsByFilter;
}

const INITIAL_STATE: LocalOptionPathsState = { pathsByFilter: {} };

const hasPersistedPaths = (value: unknown): value is LocalOptionPathsState =>
  typeof value === 'object' &&
  value !== null &&
  isLocalOptionPathsByFilter((value as Partial<LocalOptionPathsState>).pathsByFilter);

// `persist` lets storage errors escape from `setState`, so writes are guarded here.
const safeSessionStorage: StateStorage = {
  getItem: name => sessionStorage.getItem(name),
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, value);
    } catch {
      // Storage full or unavailable: the in-memory store still keeps this session correct.
    }
  },
  removeItem: name => sessionStorage.removeItem(name),
};

/**
 * Option universe of hierarchical local filters (config flag `recordsOptionPaths`).
 *
 * Their `applyFilter` needs every option path to build the query, but the option
 * query resolves after the data queries on a page load. Persisting the last
 * resolved universe lets a refresh rebuild the query it had before instead of
 * falling back to different semantics, and keeping it in a store (rather than
 * module state) lets the filter hooks re-memo once fresh options arrive.
 */
export const useLocalOptionPathsStore = create<LocalOptionPathsState>()(
  persist(() => INITIAL_STATE, {
    name: STORAGE_KEY,
    storage: createJSONStorage(() => safeSessionStorage),
    // A malformed persisted value falls back to the empty universe.
    merge: (persisted, current) =>
      hasPersistedPaths(persisted) ? { ...current, pathsByFilter: persisted.pathsByFilter } : current,
  }),
);

export const setLocalOptionPaths = (filterKey: FilterType, paths: string[]): void => {
  const { pathsByFilter } = useLocalOptionPathsStore.getState();
  if (hasSamePaths(pathsByFilter[filterKey], paths)) return;

  useLocalOptionPathsStore.setState({ pathsByFilter: { ...pathsByFilter, [filterKey]: paths } });
};

export const getLocalOptionPaths = (filterKey: FilterType): string[] =>
  useLocalOptionPathsStore.getState().pathsByFilter[filterKey] ?? EMPTY_PATHS;

export const useLocalOptionPaths = (filterKey: FilterType): string[] =>
  useLocalOptionPathsStore(state => state.pathsByFilter[filterKey] ?? EMPTY_PATHS);

export const useLocalOptionPathsByFilter = (): LocalOptionPathsByFilter =>
  useLocalOptionPathsStore(state => state.pathsByFilter);
